import React, { useState, useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import {
  MapContainer,
  TileLayer,
  Circle,
  Polygon,
  Marker as LeafletMarker,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ReactComponent as Marker } from "./gps.svg";
import Button from "@material-ui/core/Button";
import * as turf from "@turf/turf";
import { GeoSearchControl, OpenStreetMapProvider } from "leaflet-geosearch";
import { ReactComponent as Locator } from "./location.svg";

import "./mapSearchStyles.css";

const Search = ({ provider, language }) => {
  const map = useMap();
  useEffect(() => {
    const searchControl = new GeoSearchControl({
      provider,
      showMarker: false,
      searchLabel:
        language === "arabic" ? "ابحث عن عنوان" : "Search for address",
      classNames: {
        input: language === "arabic" ? "text-right pr-5" : "text-left",
      },
    });
    map.addControl(searchControl);
    return () => map.removeControl(searchControl);
  }, [map, provider, language]);

  return null;
};

function MapPicker({
  language,
  defaultPosition,
  savePosition,
  countryObj,
  selectedAreaObj,
  themeColor,
}) {
  const [map, setMap] = useState(null);
  const [position, setPosition] = useState(
    defaultPosition || { lat: 29.38, lng: 47.99 }
  );
  const [saveDisabled, setSaveDisabled] = useState(true);

  // Update position when map moves
  const onMove = useCallback(() => {
    if (!map) return;
    setPosition(map.getCenter());
    if (saveDisabled) setSaveDisabled(false);
  }, [map, saveDisabled]);

  useEffect(() => {
    if (!map) return;
    map.on("move", onMove);
    return () => map.off("move", onMove);
  }, [map, onMove]);

  const getCurrentPosition = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (res) => {
          const lat = res.coords.latitude;
          const lng = res.coords.longitude;
          setPosition({ lat, lng });
          if (map) map.flyTo({ lat, lng });
        },
        () => {},
        { enableHighAccuracy: true, timeout: 6000 }
      );
    }
  };

  // Check if the current point is inside any allowed shape
  const isPointInsideAllowedArea = () => {
    if (!selectedAreaObj?.shapes || selectedAreaObj?.shapes?.length === 0)
      return true;
    if (!selectedAreaObj?.shapes || !position) return false;

    const pt = turf.point([position.lng, position.lat]);

    return selectedAreaObj.shapes.some((shape) => {
      if (shape.type === "Polygon" || shape.type === "Rectangle") {
        let coords = shape.points.map((p) => [p.lng, p.lat]);
        // Ensure polygon is closed
        const first = coords[0];
        const last = coords[coords.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) {
          coords.push(first);
        }
        const poly = turf.polygon([coords]);
        return turf.booleanPointInPolygon(pt, poly);
      }
      if (shape.type === "Circle") {
        const center = turf.point([shape.center.lng, shape.center.lat]);
        const dist = turf.distance(pt, center, { units: "meters" });
        return dist <= shape.radius; // radius in meters
      }
      if (shape.type === "Marker") {
        const marker = turf.point([shape.point.lng, shape.point.lat]);
        const dist = turf.distance(pt, marker, { units: "meters" });
        return dist <= 10; // allow 10m tolerance for marker point
      }
      return false;
    });
  };

  const displayMap = useMemo(
    () => (
      <MapContainer
        center={[position.lat, position.lng]}
        zoom={15}
        scrollWheelZoom
        whenCreated={setMap}
        style={{ height: "calc(100vh - 117px)" }}
      >
        {!!map && (
          <Search
            language={language}
            provider={
              new OpenStreetMapProvider({
                params: {
                  "accept-language": language === "arabic" ? "ar" : "en",
                  countrycodes: countryObj?.alpha_2_code || [
                    "kw",
                    "qa",
                    "bh",
                    "sa",
                    "om",
                    "ae",
                  ],
                },
              })
            }
          />
        )}
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        {/* Draw all allowed shapes */}
        {selectedAreaObj?.shapes?.map((shape, idx) => {
          if (shape.type === "Polygon" || shape.type === "Rectangle") {
            return (
              <Polygon
                key={idx}
                positions={shape.points.map((p) => [p.lat, p.lng])}
                pathOptions={{ color: themeColor, fillOpacity: 0.1 }}
              />
            );
          }
          if (shape.type === "Circle") {
            return (
              <Circle
                key={idx}
                center={[shape.center.lat, shape.center.lng]}
                radius={shape.radius}
                pathOptions={{ color: themeColor, fillOpacity: 0.1 }}
              />
            );
          }
          if (shape.type === "Marker") {
            return (
              <LeafletMarker
                key={idx}
                position={[shape.point.lat, shape.point.lng]}
              />
            );
          }
          return null;
        })}

        <Marker
          style={{
            width: 60,
            height: 60,
            position: "absolute",
            left: -2,
            right: 0,
            top: -50,
            bottom: 0,
            margin: "auto",
            zIndex: 400,
          }}
        />
      </MapContainer>
    ),
    [map, position, language, selectedAreaObj, countryObj]
  );

  const inside = isPointInsideAllowedArea();

  return (
    <div>
      {displayMap}

      {/* Locator button */}
      <div
        style={{
          width: 50,
          height: 50,
          position: "absolute",
          right: 10,
          top: 70,
          background: "white",
          borderRadius: 2,
          cursor: "pointer",
          zIndex: 401,
          border: "2px solid rgba(0,0,0,0.2)",
        }}
        onClick={getCurrentPosition}
      >
        <Locator
          style={{
            width: 25,
            height: 25,
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            fill: "#606060",
            margin: "auto",
          }}
        />
      </div>

      {/* Save button */}
      <div
        style={{
          backgroundColor: "white",
          paddingBottom: 8,
          marginBottom: 0,
          height: 60,
          zIndex: 401,
        }}
        className={
          language === "english"
            ? "action-button-english"
            : "action-button-arabic"
        }
      >
        <Button
          className="mb-1 ml-1 mx-auto"
          variant="contained"
          color="primary"
          disableFocusRipple={!inside}
          disableRipple={!inside}
          style={{
            width: "97%",
            height: "100%",
            boxShadow: "none",
            textTransform: "none",
            backgroundColor: !inside ? "#9c9c9c" : undefined,
            fontSize: "1rem",
          }}
          dir={language === "english" ? "ltr" : "rtl"}
          onClick={() => inside && savePosition(position)}
        >
          {!inside
            ? language === "arabic"
              ? `العنوان غير مسموح في ${selectedAreaObj?.ar_name || "المنطقة"}`
              : `Location is outside ${selectedAreaObj?.name || "the area"}`
            : language === "arabic"
            ? "حفظ"
            : "Save"}
        </Button>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  areas: state.store.store.areas,
  language: state.store.language,
  branch_delivery_charges: state.store.store.branch_delivery_charges,
  branch: state.branch,
  settings: state.store.store.settings,
  address: state.customers.address,
  themeColor: state.store.store.settings.dark || "#0099cc",
});

export default connect(mapStateToProps)(MapPicker);
